import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import { Evaluation, Test } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import GroupedContent from '@/shared/ui/layout/GroupedContent';
import { LinkCard, CardTitle, CardDescription, CardSection } from '@/shared/ui/card';
import ModelNameInput from '@/shared/ui/inputs/ModelNameInput';
import { calculateOverallPercentage } from '@/shared/utils/scoreCalculation';
import './styles.css';

const EvaluationList: React.FC = () => {
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState<Evaluation[]>([]);
  const [modelFilter, setModelFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const data = await api.evaluations.getAll();
      setEvaluations(data);
      setFilteredEvaluations(data);
      setError(null);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch evaluations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluations();
  }, []);

  // Filter evaluations when model filter changes
  useEffect(() => {
    if (!modelFilter.trim()) {
      setFilteredEvaluations(evaluations);
      return;
    }

    const filtered = evaluations.filter(evaluation => 
      evaluation.modelName.toLowerCase().includes(modelFilter.toLowerCase())
    );
    setFilteredEvaluations(filtered);
  }, [modelFilter, evaluations]);

  const handleModelFilterChange = (value: string) => {
    setModelFilter(value);
  };

  const clearFilter = () => {
    setModelFilter('');
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTestName = (test: string | Test): string => {
    return typeof test === 'string' ? test : test.name;
  };

  const calculateScore = (evaluation: Evaluation): number => {
    if (!evaluation.results.length) return 0;
    return calculateOverallPercentage(evaluation.results);
  };

  const calculateCompletion = (evaluation: Evaluation): number => {
    if (!evaluation.results.length) return 0;
    const completedTasks = evaluation.results.filter(result => result.score !== null).length;
    return Math.round((completedTasks / evaluation.results.length) * 100);
  };

  // Group evaluations by test name
  const groupEvaluationsByTest = (evaluations: Evaluation[]) => {
    return evaluations.reduce((acc, evaluation) => {
      const testName = getTestName(evaluation.test);
      if (!acc[testName]) {
        acc[testName] = [];
      }
      acc[testName].push(evaluation);
      return acc;
    }, {} as Record<string, Evaluation[]>);
  };

  // Sort evaluations within each group by date
  const sortEvaluations = (evaluations: Evaluation[]) => {
    return [...evaluations].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA; // Most recent first
    });
  };

  const renderEvaluationGroup = (testName: string, evaluations: Evaluation[]) => {
    const sortedEvaluations = sortEvaluations(evaluations);
    
    return (
      <div className="evaluations-grid">
        {sortedEvaluations.map(evaluation => {
          const score = evaluation.status === 'completed' 
            ? calculateScore(evaluation)
            : calculateCompletion(evaluation);

          const testId = typeof evaluation.test === 'string' 
            ? evaluation.test 
            : evaluation.test._id;

          return (
            <LinkCard 
              key={evaluation._id} 
              to={evaluation.status === 'in-progress' 
                ? `/tests/${testId}/evaluate/${evaluation._id}`
                : `/evaluations/${evaluation._id}`
              }
            >
              <CardTitle title={evaluation.modelName} />
              <CardDescription variant="meta">
                <span className={`status ${evaluation.status}`}>
                  {evaluation.status}
                </span>
                <span className="date">
                  {formatDate(evaluation.createdAt)}
                </span>
              </CardDescription>
              <CardSection>
                <div className="score-bar">
                  <div 
                    className={`score-progress ${evaluation.status === 'in-progress' ? 'in-progress' : ''}`}
                    style={{ width: `${score}%` }}
                  />
                  <span className="score-text">
                    {evaluation.status === 'completed' 
                      ? `Score: ${score}%`
                      : `Completion: ${score}%`
                    }
                  </span>
                </div>
              </CardSection>
            </LinkCard>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading evaluations...</div>;
  if (error) return <div className="error">{error}</div>;

  const groupedEvaluations = groupEvaluationsByTest(filteredEvaluations);
  const emptyMessage = modelFilter
    ? `No evaluations found for model "${modelFilter}".`
    : "No evaluations found. Go to a test and click 'Evaluate Test' to start a new evaluation.";

  return (
    <PageLayout
      title="Evaluations"
      actions={
        <div className="test-selector">
          <ModelNameInput
            value={modelFilter}
            onChange={handleModelFilterChange}
            id="modelFilter"
            label=""
          />
          {modelFilter && (
            <button onClick={clearFilter} className="clear-filter">
              Clear Filter
            </button>
          )}
        </div>
      }
    >
      {Object.keys(groupedEvaluations).length === 0 ? (
        <div className="evaluations-empty">{emptyMessage}</div>
      ) : (
        <GroupedContent
          groups={groupedEvaluations}
          renderGroup={renderEvaluationGroup}
          emptyMessage={emptyMessage}
        />
      )}
    </PageLayout>
  );
};

export default EvaluationList; 